import * as THREE from 'three';
import {isSketchLineObject} from '../../../mesh/guards/isSketchLineObject';
import {AbstractSketchNearestPointProjector} from './AbstractSketchNearestPointProjector';

/**
 * 草图有限直线最近点投影器。
 *
 * 职责：普通直线按正式起终点完成有限线段投影，无限构造线按其权威直线方向投影。
 */
export class SketchLineNearestPointProjector extends AbstractSketchNearestPointProjector {
    /** 复用的线段方向。 */
    private readonly segmentDirection = new THREE.Vector3();

    /** 复用的起点到鼠标向量。 */
    private readonly startToMouse = new THREE.Vector3();

    /** 复用的投影结果。 */
    private readonly projectedPoint = new THREE.Vector3();

    /**
     * 判断目标是否为草图直线。
     * @param targetObject 待判断对象。
     * @returns 目标为草图直线时返回 true。
     */
    public supports(targetObject: THREE.Object3D): boolean {
        return isSketchLineObject(targetObject);
    }

    /**
     * 将世界坐标投影到有限直线段。
     * @param targetObject 目标草图直线。
     * @param worldPosition 待投影世界坐标。
     * @returns 线段上的最近世界坐标。
     */
    public project(targetObject: THREE.Object3D, worldPosition: THREE.Vector3): THREE.Vector3 | null {
        if (!isSketchLineObject(targetObject)) {
            return null;
        }

        const startPosition = targetObject.startPoint.getPosition();
        const endPosition = targetObject.endPoint.getPosition();
        this.segmentDirection.subVectors(endPosition, startPosition);
        const segmentLengthSquared = this.segmentDirection.lengthSq();
        if (segmentLengthSquared <= Number.EPSILON) {
            return startPosition;
        }

        this.startToMouse.subVectors(worldPosition, startPosition);
        const rawParameter = this.startToMouse.dot(this.segmentDirection) / segmentLengthSquared;
        const parameter = targetObject.isInfiniteLength
            ? rawParameter
            : THREE.MathUtils.clamp(rawParameter, 0, 1);
        return this.projectedPoint.copy(startPosition)
            .addScaledVector(this.segmentDirection, parameter)
            .clone();
    }
}
